<template>
  <b-container fluid class="m-0 p-0">
    <!-- Terapie in atto: testa di sezione con le azioni, poi una riga per
         terapia (icona del ciclo, prodotto, patologia e assunzioni, scadenza) -->
    <div class="mb-4">
      <div class="section-head mb-3">
        <h2 class="section-title mb-0">{{$t('TerapieComponent.activeTitle')}}</h2>
        <div class="filter-bar__actions ml-auto">
          <b-button
            v-if="therapies && therapies.active && therapies.active.length > 0"
            size="sm"
            variant="outline-secondary"
            @click="prospettoDownload()"
          >
            <b-icon icon="download" aria-hidden="true"></b-icon>
            <span class="d-none d-md-inline ml-1">{{$t('TerapieComponent.downloadProspetto')}}</span>
          </b-button>
          <b-button
            v-if="user.permessi.includes('alimentare scheda paziente')"
            size="sm"
            @click="goToNew"
          >
            <b-icon icon="plus" aria-hidden="true"></b-icon> {{$t('TerapieComponent.newTherapy')}}
          </b-button>
        </div>
      </div>
      <div v-if="therapies.active && therapies.active.length > 0" class="list-stack">
        <div
          v-for="(ter, ind) in therapies.active"
          :key="ind"
          class="list-row point"
          role="button"
          tabindex="0"
          :aria-label="$t('TerapieComponent.vedi_terapia') + ': ' + ter.product_name"
          @click="selectTherapy(ter)"
          @keydown.enter.prevent="selectTherapy(ter)"
          @keydown.space.prevent="selectTherapy(ter)"
        >
          <span class="list-row__icon" :title="ter.tipo">
            <b-icon :icon="cycleIcons[ter.tipo]" aria-hidden="true"></b-icon>
          </span>
          <div class="list-row__body">
            <div class="list-row__title">{{ ter.product_name }}</div>
            <div class="list-row__meta">
              <span class="text-capitalize">{{ ter.tipo }}</span>
              <span v-if="ter.pathology"> · {{ ter.pathology.name }}</span>
              <span v-if="getAssunzioni(ter)"> · {{ getAssunzioni(ter) }}</span>
            </div>
          </div>
          <span class="list-row__aside">
            <b-badge v-if="ter.suspended" pill variant="warning">{{$t('TerapieComponent.suspended')}}</b-badge>
            <template v-else-if="ter.end_at">{{$t('TerapieComponent.until')}} {{ formatData(ter.end_at) }}</template>
            <template v-else-if="ter.start_at">{{$t('TerapieComponent.since')}} {{ formatData(ter.start_at) }}</template>
          </span>
          <div class="list-row__actions">
            <b-button
              v-if="ter.own"
              variant="white"
              class="btn-icon btn-sm"
              :title="$t('TerapieComponent.elimina')"
              :aria-label="$t('TerapieComponent.elimina_terapia')"
              @click.stop="showDelModal(ter)"
              @keydown.enter.stop
              @keydown.space.stop
            >
              <b-icon icon="trash" aria-hidden="true"></b-icon>
            </b-button>
            <b-icon icon="chevron-right" class="list-row__chevron" aria-hidden="true"></b-icon>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        <span class="cc-icon cc-icon--terapia empty-state__icon" aria-hidden="true"></span>
        <p>{{$t('ArmadiettoComponent.nessunaInAtto')}}</p>
      </div>
    </div>
    <!-- Storico: le terapie chiuse, in righe tenui -->
    <div>
      <div class="section-head mb-3">
        <h2 class="section-title mb-0">{{$t('TerapieComponent.historyTitle')}}</h2>
      </div>
      <div v-if="therapies.ended && therapies.ended.length > 0" class="list-stack">
        <div
          v-for="(ter, ind) in therapies.ended"
          :key="ind"
          class="list-row point list-row--muted"
          role="button"
          tabindex="0"
          :aria-label="$t('TerapieComponent.vedi_terapia') + ': ' + ter.product_name"
          @click="selectTherapy(ter)"
          @keydown.enter.prevent="selectTherapy(ter)"
          @keydown.space.prevent="selectTherapy(ter)"
        >
          <span class="list-row__icon" :title="ter.tipo">
            <b-icon :icon="cycleIcons[ter.tipo]" aria-hidden="true"></b-icon>
          </span>
          <div class="list-row__body">
            <div class="list-row__title">{{ ter.product_name }}</div>
            <div class="list-row__meta">
              <span class="text-capitalize">{{ ter.tipo }}</span>
              <span v-if="ter.pathology"> · {{ ter.pathology.name }}</span>
              <span v-if="getAssunzioni(ter)"> · {{ getAssunzioni(ter) }}</span>
            </div>
          </div>
          <span class="list-row__aside">
            <template v-if="ter.suspended">{{$t('TerapieComponent.suspended')}}</template>
            <template v-else-if="ter.end_at">{{$t('TerapieComponent.finished')}} {{ formatData(ter.end_at) }}</template>
          </span>
          <div class="list-row__actions">
            <b-button
              v-if="ter.own"
              variant="white"
              class="btn-icon btn-sm"
              :title="$t('TerapieComponent.elimina')"
              :aria-label="$t('TerapieComponent.elimina_terapia')"
              @click.stop="showDelModal(ter)"
              @keydown.enter.stop
              @keydown.space.stop
            >
              <b-icon icon="trash" aria-hidden="true"></b-icon>
            </b-button>
            <b-icon icon="chevron-right" class="list-row__chevron" aria-hidden="true"></b-icon>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        <b-icon icon="clock-history" class="empty-state__icon" aria-hidden="true"></b-icon>
        <p>{{$t('TerapieComponent.noHistory')}}</p>
      </div>
    </div>

    <!-- Dettaglio o modifica della terapia scelta -->
    <b-modal
      size="xl"
      centered
      id="edit-therapy"
      hide-footer
      :title="editing ? $t('ArmadiettoComponent.modificaTerapia') : $t('ArmadiettoComponent.dettaglioTerapia')"
    >
      <div v-if="selectedTherapy" class="text-left">
        <EditTherapy
          v-if="editing"
          :therapy="selectedTherapy"
          v-on:cancel-operation="$bvModal.hide('edit-therapy')"
          v-on:save-therapy="$bvModal.hide('edit-therapy')"
        />
        <template v-else>
          <RecapTherapy :therapy="selectedTherapy" />
          <div class="filter-bar__actions justify-content-end mt-3">
            <!-- Bottone di modifica -->
            <b-button v-if="selectedTherapy.own" size="sm" @click="editing = true">
              <b-icon icon="pencil" aria-hidden="true"></b-icon>
              {{$t('TerapieComponent.edit')}}
            </b-button>
            <!-- Ripresa o sospensione della terapia -->
            <b-button
              v-if="selectedTherapy.suspended && !selectedTherapy.terminata"
              size="sm"
              variant="outline-secondary"
              @click="openSuspModal(false)"
            >
              <b-icon icon="play-circle" aria-hidden="true"></b-icon>
              {{$t('TerapieComponent.resume')}}
            </b-button>
            <b-button
              v-else-if="!selectedTherapy.terminata"
              size="sm"
              variant="outline-secondary"
              @click="openSuspModal(true)"
            >
              <b-icon icon="pause-circle" aria-hidden="true"></b-icon>
              {{$t('TerapieComponent.suspend')}}
            </b-button>
            <!-- Download del promemoria -->
            <b-button size="sm" variant="outline-secondary" @click="downloadRecap">
              <b-icon icon="download" aria-hidden="true"></b-icon>
              {{$t('TerapieComponent.downloadReminder')}}
            </b-button>
          </div>
        </template>
        <p class="list-row__meta mt-3 mb-0">
          {{$t('TerapieComponent.createdAt')}} {{ formatData(selectedTherapy.created_at) }}
        </p>
      </div>
    </b-modal>
    <b-modal
      id="susp-therapy"
      size="lg"
      centered
      :title="$t('TerapieComponent.conferma')"
      button-size="sm"
      :cancel-title="$t('TerapieComponent.no_annulla')"
      cancel-variant="popup-button"
      :ok-title="$t('TerapieComponent.si_conferma')"
      ok-variant="popup-button"
      footer-class="border-0"
      @ok="doSuspend"
      @cancel="$bvModal.hide('edit-therapy')"
    >
      {{ $t('TerapieComponent.testoConfermaSospensione', {
        azione: $t(isSuspending ? 'TerapieComponent.azioneSospendere' : 'TerapieComponent.azioneRiprendere')
      }) }}
    </b-modal>
    <b-modal
      id="del-therapy"
      size="lg"
      centered
      :title="$t('TerapieComponent.deleteConfirmTitle')"
      button-size="sm"
      :cancel-title="$t('TerapieComponent.no_annulla')"
      cancel-variant="popup-button"
      :ok-title="$t('TerapieComponent.si_conferma')"
      ok-variant="popup-button"
      footer-class="border-0"
      @ok="doDelete"
      @cancel="$bvModal.hide('edit-therapy')"
    >
      {{$t('TerapieComponent.deleteConfirmText')}}
    </b-modal>
    <b-modal
      id="new_t_inpage"
      size="xl"
      centered
      :title="$t('TerapieComponent.nuovaTerapia')"
      hide-footer
      scrollable
      @hidden="fetchTherapies"
    >
      <NewTherapy :subject="subject" @save-therapy="handleNewTherapy($event)" />
    </b-modal>
  </b-container>
</template>
<script>
import { mapState, mapActions } from "vuex";
import { cittadinoService } from "@/_services";
import { supportService } from "@/_services";
import * as moment from "moment/moment";
import RecapTherapy from "@/app/modules/servizi/gestionePazienti/components/RecapTherapy.vue";
import EditTherapy from "@/app/modules/servizi/profiloSanitario/components/EditTherapy.vue";
import NewTherapy from "@/app/modules/servizi/profiloSanitario/components/NewTherapy.vue";
export default {
  name: "TerapieComponent",
  props: ["subject", "ricognizione"],
  components: { RecapTherapy, EditTherapy, NewTherapy },
  data() {
    return {
      cycleIcons: {
        periodica: "calendar",
        cronica: "watch",
        occasionale: "lightning",
      },
      therapies: { active: [], ended: [] },
      typesColors: ["ter-4", "ter-1", "ter-2"],
      selectedTherapy: null,
      editing: false,
      isSuspending: false,
    };
  },
  computed: {
    ...mapState({
      alert: (state) => state.alert,
    }),
    ...mapState("utente", ["status", "user"]),
    // modalTitle: function () {
    //   if (this.selectedTherapy && this.selectedTherapy.pathology) {
    //     return "Terapia per " + this.selectedTherapy.pathology.name
    //   } else if (this.editing && this.selectTherapy) {
    //     return "Terapia per patologia non indicata"
    //   } else return ""
    // }
  },
  methods: {
    ...mapActions({
      clearAlert: "alert/clear",
      errorAlert: "alert/error",
      successAlert: "alert/success",
      infoAlert: "alert/info",
    }),
    prospettoDownload() {
      var self = this;
      // Determina se siamo su un familiare dalla route
      const isFamiliare = this.$route.path.includes('/familiari-assistiti/');
      const subjectType = isFamiliare ? 'familiare' : 'user_in_app';
      
      supportService
        .downloadProspettoTerapia(this?.subject?.hashid, subjectType)
        .then(function (response) {
          console.log(self.subject.hashid, 'Type:', subjectType);
          let blob = new Blob([response.data]);
          // let blob = new Blob([response.data], { type: "text/csv" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "ProspettoTerapeutico.pdf";
          link.click();
        })
        .catch(function (err) {
          // console.log(err);
          window.scrollTo(0, 0);
          self.errorAlert(self.$t('TerapieComponent.downloadError'));
        });
    },
    goToNew() {
      if (this.ricognizione) {
        return this.$bvModal.show("new_t_inpage");
      }
      
      // Determina se siamo su un familiare dalla route
      const isFamiliare = this.$route.path.includes('/familiari/') || this.$route.path.includes('/familiari-assistiti/');
      
      var destination = {
        name: "nuova terapia",
      };
      if (this.subject && this.user.type === 1) {
        const queryParams = {
          sub: this.subject.hashid,
          type: isFamiliare ? 'familiare' : this.subject.type,
        };
        
        // Se è un familiare, aggiungi l'hashid del caregiver per il ritorno
        if (isFamiliare) {
          // Prova a recuperare l'hash del caregiver dalla route
          const caregiverHash = this.$route.params.hash || this.$route.params.hashid;
          if (caregiverHash) {
            queryParams.caregiver = caregiverHash;
          }
        }
        
        destination = {
          name: "nuova terapia Paziente",
          query: queryParams,
        };
      } else if (this.subject && this.user.type === 0) {
        destination = {
          name: "nuova terapia assistito",
          query: {
            sub: this.subject.hashid,
            type: this.subject.type,
          },
        };
      }
      this.$router.push(destination);
    },
    showDelModal(ter) {
      this.selectedTherapy = ter;
      this.$bvModal.show("del-therapy");
    },
    selectTherapy(ter) {
      this.selectedTherapy = ter;
      this.$bvModal.show("edit-therapy");
    },
    fetchTherapies() {
      if (this.subject) {
        return this.fetchUserTherapies();
      }
      this.fetchOwnTherapies();
    },
    fetchOwnTherapies() {
      var self = this;
      cittadinoService
        .getTherapies()
        .then(function (response) {
          self.therapies = response.data.data;
        })
        .catch(function (error) {
          self.errorAlert(self.$t('TerapieComponent.fetchError'));
        });
    },
    fetchUserTherapies() {
      var self = this;
      var filters = {
      user: this.subject.hashid,
      subject_type: this.subject.type,
      };
      cittadinoService
        .getTherapies(filters)
        .then(function (response) {
          self.therapies = response.data.data;
          // console.log('user', self.subject.hashid)
          // console.log('subject', self.subject.type)
          // console.log('user therapies', self.therapies)
        })
        .catch(function (error) {
          self.errorAlert(self.$t('TerapieComponent.fetchError'));
        });
    },
    openSuspModal(isSuspending) {
      this.$bvModal.show("susp-therapy");
      this.isSuspending = isSuspending;
    },
    doSuspend() {
      var self = this;
      var form = this.selectedTherapy;
      form.suspended = this.isSuspending;
      var term = self.isSuspending ? self.$t('TerapieComponent.suspended') : self.$t('TerapieComponent.resumed');
      cittadinoService
        .updateTherapy(form)
        .then(function (response) {
          self.$bvModal.hide("edit-therapy");
          self.successAlert(self.$t('TerapieComponent.therapy') + ' ' + term);
          self.fetchTherapies();
        })
        .catch(function (error) {
          self.$bvModal.hide("edit-therapy");
          self.errorAlert(self.$t('TerapieComponent.operationFailed'));
        });
    },
    doDelete() {
      var self = this;
      var form = { hashid: this.selectedTherapy.hashid };
      cittadinoService
        .deleteTherapy(form)
        .then(function (response) {
          self.$bvModal.hide("edit-therapy");
          self.successAlert(self.$t('TerapieComponent.deleted'));
          self.fetchTherapies();
        })
        .catch(function (error) {
          self.$bvModal.hide("edit-therapy");
          self.errorAlert(self.$t('TerapieComponent.deleteError'));
        });
    },
    formatData(data) {
      return moment(data).format("DD/MM/YYYY");
    },
    downloadRecap() {
      var self = this;
      cittadinoService
        .therapyRecap(this.selectedTherapy.hashid)
        .then(function (response) {
          self.$bvModal.hide("edit-therapy");
          let blob = new Blob([response.data]);
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "promemoria_terapia.pdf";
          link.click();
        })
        .catch(function (error) {
          self.$bvModal.hide("edit-therapy");
          self.errorAlert(self.$t('TerapieComponent.downloadReminderError'));
        });
    },
    getAssunzioni(t) {
      const assunzioniKeys = [
        "mattina",
        "meta_mattina",
        "pranzo",
        "pomeriggio",
        "sera",
        "notte",
      ];
      let assunzioniString = assunzioniKeys.reduce((acc, key) => {
        if (t[key]) {
          acc += key + " ";
        }
        return acc;
      }, "");

      if (assunzioniString == " ") return null;

      assunzioniString = assunzioniString.replace(
        "meta_mattina",
        "metà mattina"
      );

      return assunzioniString || null;
    },
    handleNewTherapy(newTp) {
      this.therapies.active.push(newTp);
      this.$bvModal.hide("new_t_inpage");
    },
  },
  created() {
    this.fetchTherapies();
  },
  mounted() {
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      this.selectedTherapy = null;
      this.editing = false;
    });
  },
};
</script>
